import { createContext, useContext } from 'react';
import { CVBuilderFormValues } from 'utils/cv-form-db';

/**
 * Transport-agnostic data port for the CV builder wizard.
 *
 * The wizard (steps, navigation, summary) knows nothing about Auth0, IndexedDB, or
 * which backend endpoint it talks to — it only talks to this port. Two providers
 * implement it:
 *  - `AuthenticatedCvDataProvider`: logged-in talent (Auth0 token + IndexedDB draft +
 *    `/curriculumVitaes`).
 *  - `GuestCvDataProvider`: external applicant with a magic-link session token, fully
 *    server-backed (`/guest/curriculumVitaes`), no local storage.
 *
 * `completedSteps` travels inside `CVBuilderFormValues`, so `saveDraft` needs no extra
 * argument; the guest provider forwards it to the server for resume + the operator funnel.
 */
export interface CvBuilderData {
  /**
   * Seed values for Formik, or `null` until the source has loaded. The wizard renders
   * nothing until this is non-null. Each provider sets it EXACTLY ONCE (null → object)
   * so Formik's `enableReinitialize` does not wipe in-progress edits on later renders.
   */
  initialValues: CVBuilderFormValues | null;
  /**
   * Persist the current form (autosave on navigation / list edits / edit-mode save).
   * Fire-and-forget: the wizard navigates without awaiting it, so implementations MUST
   * NOT reject — they own their own retry/error handling (the guest provider routes a
   * dead session to the expired screen; both log and swallow other failures).
   */
  saveDraft: (values: CVBuilderFormValues) => Promise<void>;
  /**
   * Timestamp (ms) of the last SUCCESSFUL persist, or null if nothing has been saved yet
   * in this session. Exists so the wizard can say so: both providers autosave on every
   * forward navigation and list edit, and until now that was completely invisible — on a
   * ten-minute form, on a phone, "is my work safe?" is a question the UI should answer.
   */
  lastSavedAt: number | null;
  /**
   * True when the most recent persist attempt failed and was not recovered.
   *
   * Deliberately paired with `lastSavedAt`: the guest autosave swallows non-terminal
   * failures as noise (a per-entry validation rejection fails the WHOLE payload in
   * pro-core's `CreateCvHttpValidator`), so a talent could keep typing into a form that had
   * silently stopped persisting. Surfacing "saved" without also surfacing "not saved" would
   * make that worse, not better.
   */
  saveFailed: boolean;
  /** Reload the last-persisted form (edit-mode discard restores from here). */
  loadDraft: () => Promise<CVBuilderFormValues | undefined>;
  /** Drop the working draft (authenticated: clear IndexedDB; guest: no-op, server-owned). */
  clearDraft: () => Promise<void>;
  /**
   * True when the talent already has a saved CV on the server. The wizard uses this to
   * pick the landing step when none is requested via the URL: an existing CV opens on
   * the Summary (view + per-section edit), a fresh start opens on the first step. The
   * guest provider leaves this `false` — it has its own resume/landing logic.
   */
  hasExistingCv: boolean;
  /** Finalize the CV (authenticated: create/update; guest: save + submit-terminal). */
  submit: (values: CVBuilderFormValues) => Promise<void>;
  /** True while `submit` is in flight (drives the Summary button's disabled/spinner state). */
  isSubmitting: boolean;
  /**
   * True once `submit` has succeeded — the wizard swaps to a terminal confirmation
   * screen and accepts no further edits. The authenticated provider flips this; the
   * guest flow leaves it `false` and renders its own page-level terminal
   * (`GuestStatusScreen`, via `markSubmitted`) when the session turns `submitted`.
   */
  submitted: boolean;
}

export const CvBuilderDataContext = createContext<CvBuilderData | undefined>(
  undefined
);

export const useCvBuilderData = (): CvBuilderData => {
  const context = useContext(CvBuilderDataContext);
  if (!context) {
    throw new Error(
      'useCvBuilderData must be used within a CvBuilderData provider ' +
        '(AuthenticatedCvDataProvider or GuestCvDataProvider)'
    );
  }
  return context;
};
